<template>
  <div class="dialog">
    <span class="cls" @click="oth_showAlarmList(false)"></span>
    <div class="paper">

      <div class="header">
        <p @click="oth_showAlarmList(false)"><i class="ph-bold ph-arrow-left"></i></p>
        <h2>알림</h2>
        <p @click="toggleMenu"><i class="ph-bold ph-dots-three"></i></p>

        <!-- 더보기 메뉴 -->
        <Transition name="menu-fade">
          <div class="header-menu" v-if="showMenu" @click.stop>
            <button @click="markAllRead">
              <i class="ph ph-checks"></i> 모두 읽음 처리
            </button>
            <button @click="deleteAllRead">
              <i class="ph ph-trash"></i> 읽은 알림 삭제
            </button>
          </div>
        </Transition>
      </div>

      <!-- 알림 리스트 -->
      <ul class="notice-list" v-if="notice_ary.length">
        <li
          v-for="item in notice_ary"
          :key="item.id"
          :class="{ unread: !item.is_read }"
          @click="handleClick(item)"
        >
          <!-- 안읽음 인디케이터 -->
          <span class="unread-dot" v-if="!item.is_read"></span>

          <!-- 프로필/타입 이미지 -->
          <div class="avatar" :class="'type-' + item.type">
            <img
              v-if="item.image"
              :src="item.image"
              alt=""
              @error="(e) => e.target.style.display = 'none'"
            />
            <i v-else :class="getTypeIcon(item.type)"></i>
          </div>

          <!-- 본문 -->
          <div class="content">
            <p class="message" v-html="item.message"></p>
            <span class="time">{{ formatTime(item.created_at) }}</span>
          </div>
        </li>
      </ul>

      <!-- 빈 상태 -->
      <div class="empty" v-else>
        <i class="ph ph-bell-slash"></i>
        <p>알림이 없습니다</p>
      </div>

    </div>

    <!-- 메뉴 바깥 클릭 시 닫기 -->
    <div class="menu-backdrop" v-if="showMenu" @click="showMenu = false"></div>
  </div>
</template>

<script>
import { inject, ref, onMounted } from "vue";

export default {
  name: "AlarmList",
  props: {
    oth_showAlarmList: {
      type: Function,
      required: true
    }
  },
  setup() {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');

    const notice_ary = ref([]);
    const showMenu = ref(false);

    // ── 알림 타입별 아이콘 ──
    function getTypeIcon(type) {
      const icons = {
        event:   'ph ph-megaphone',
        comment: 'ph ph-chat-circle-dots',
        like:    'ph ph-heart',
        post:    'ph ph-article',
        branch:  'ph ph-buildings',
        system:  'ph ph-gear',
      };
      return icons[type] || 'ph ph-bell';
    }

    // ── 상대 시간 포맷 ──
    function formatTime(dateStr) {
      if (!dateStr) return '';
      const now = new Date();
      const date = new Date(dateStr);
      const diff = Math.floor((now - date) / 1000); // seconds

      if (diff < 60) return '방금';
      if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;

      // 오늘/어제 판단
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (dateOnly.getTime() === today.getTime()) return '오늘';
      if (dateOnly.getTime() === yesterday.getTime()) return '어제';

      // 올해면 MM.DD, 아니면 YY.MM.DD
      const yy = String(date.getFullYear()).slice(2);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      if (date.getFullYear() === now.getFullYear()) return `${mm}.${dd}`;
      return `${yy}.${mm}.${dd}`;
    }

    // ── 알림 메시지 생성 ──
    function buildMessage(item) {
      const sender = item.sender_name ? `<b>${item.sender_name}</b>` : '';
      switch (item.type) {
        case 'comment':
          return sender ? `${sender} 님이 댓글을 달았습니다.` : (item.message || '새 댓글이 달렸습니다.');
        case 'like':
          return sender ? `${sender} 님이 좋아요를 눌렀습니다.` : (item.message || '좋아요를 받았습니다.');
        case 'milestone':
          return item.message || item.title || '🎉 마일스톤을 달성했습니다!';
        default:
          return item.message || item.title || '새 알림이 있습니다.';
      }
    }

    // ── 알림 이미지 결정 ──
    function resolveImage(item) {
      // sender 프로필 사진이 있으면 사용
      if (item.sender_image) return item.sender_image;
      // 시스템/이벤트/마일스톤 알림은 로고
      if (['system', 'notice', 'milestone'].includes(item.type)) return '/imgs/LOGO.png';
      return null; // 아이콘 fallback
    }

    // ── DB 조회 ──
    async function fetchNotice() {
      const mbid = getMbid();
      if (!mbid) return;

      try {
        const res = await sql_query(
          `SELECT n.*, 
                  m.nickname AS sender_name, 
                  m.image AS sender_image
           FROM notifications n
           LEFT JOIN member m ON n.sender = m.mb_id
           WHERE n.mb_id = '${mbid}'
           ORDER BY n.created_at DESC
           LIMIT 100`
        );

        if (res && res.length) {
          notice_ary.value = res.map(item => ({
            id: item.idx,
            type: item.type || 'system',
            image: resolveImage(item),
            message: buildMessage(item),
            created_at: item.created_at,
            is_read: Number(item.is_read) === 1,
          }));
        }

        // 열람 시 읽음 처리
        await sql_query(
          `UPDATE notifications SET is_read = 1 WHERE mb_id = '${mbid}' AND is_read = 0`
        );
      } catch (e) {
        console.error('알림 조회 실패:', e);
      }
    }

    // ── 클릭 핸들러 ──
    function handleClick(item) {
      item.is_read = true;
    }

    // ── 메뉴 액션 ──
    function toggleMenu() {
      showMenu.value = !showMenu.value;
    }

    async function markAllRead() {
      showMenu.value = false;
      notice_ary.value.forEach(n => n.is_read = true);
      await sql_query(
        `UPDATE notifications SET is_read = 1 WHERE mb_id = '${getMbid()}'`
      );
    }

    async function deleteAllRead() {
      showMenu.value = false;
      await sql_query(
        `DELETE FROM notifications WHERE mb_id = '${getMbid()}' AND is_read = 1`
      );
      notice_ary.value = notice_ary.value.filter(n => !n.is_read);
    }

    onMounted(() => {
      fetchNotice();
    });

    return {
      notice_ary,
      showMenu,
      toggleMenu,
      markAllRead,
      deleteAllRead,
      formatTime,
      getTypeIcon,
      handleClick,
    };
  }
};
</script>


<style scoped>
/* ─── 다이얼로그 배경 ─── */
.dialog {
  position: absolute;
  left: 0; top: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.cls {
  width: 100%; height: 100%;
  position: absolute;
  left: 0; top: 0;
  z-index: -1;
}

/* ─── 페이퍼 ─── */
.dialog > .paper {
  width: 90%;
  max-width: 520px;
  height: 80%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─── 헤더 ─── */
.header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 48px;
  color: var(--color-text-primary);
  flex-shrink: 0;
  margin-bottom: 16px;
}

.header > p {
  aspect-ratio: 1/1;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  margin: 0; padding: 0;
  border-radius: 12px;
  transition: background 0.15s;
}

.header > p:hover {
  background: var(--color-background-light);
}

.header > h2 {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0; padding: 0;
}

/* ─── 더보기 메뉴 ─── */
.header-menu {
  position: absolute;
  top: 52px;
  right: 0;
  background: var(--color-surface, #1e1e1e);
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  z-index: 10;
  min-width: 180px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.header-menu button {
  background: none;
  border: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  padding: 10px 14px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.15s;
}

.header-menu button:hover {
  background: var(--color-background-light);
}

.header-menu button i {
  font-size: 18px;
  opacity: 0.7;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.15s ease;
}
.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.96);
}

.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9998;
}

/* ─── 알림 리스트 ─── */
ul.notice-list {
  width: 100%;
  padding: 0; margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
}

/* 스크롤바 스타일 */
ul.notice-list::-webkit-scrollbar {
  width: 4px;
}
ul.notice-list::-webkit-scrollbar-track {
  background: transparent;
}
ul.notice-list::-webkit-scrollbar-thumb {
  background: var(--color-background-light);
  border-radius: 4px;
}

ul.notice-list > li {
  position: relative;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: var(--color-surface, #1a1a1a);
  border-radius: 14px;
  box-sizing: border-box;
  gap: 14px;
  transition: background 0.15s, transform 0.1s;
}

ul.notice-list > li:hover {
  background: var(--color-background-light);
}

ul.notice-list > li:active {
  transform: scale(0.985);
}

/* 안읽음 상태 */
ul.notice-list > li.unread {
  background: color-mix(in srgb, var(--color-brand, #e8a030) 6%, var(--color-surface, #1a1a1a));
}

/* ─── 안읽음 점 ─── */
.unread-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: var(--color-brand, #e8a030);
  border-radius: 50%;
  flex-shrink: 0;
}

/* ─── 아바타 ─── */
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-light);
  color: var(--color-text-secondary);
  font-size: 20px;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 타입별 아바타 색상 */
.avatar.type-event {
  background: color-mix(in srgb, var(--color-brand, #e8a030) 20%, transparent);
  color: var(--color-brand, #e8a030);
}
.avatar.type-comment {
  background: color-mix(in srgb, #5b9bd5 20%, transparent);
  color: #5b9bd5;
}
.avatar.type-like {
  background: color-mix(in srgb, #e85d75 20%, transparent);
  color: #e85d75;
}
.avatar.type-post {
  background: color-mix(in srgb, #6bc785 20%, transparent);
  color: #6bc785;
}
.avatar.type-branch {
  background: color-mix(in srgb, var(--color-brand, #e8a030) 20%, transparent);
  color: var(--color-brand, #e8a030);
}

/* ─── 본문 ─── */
.content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
}

.content .message {
  padding: 0; margin: 0;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: keep-all;
}

.content .message :deep(b) {
  font-weight: 600;
  color: var(--color-brand, #e8a030);
}

.content .time {
  font-size: var(--font-size-sm);
  color: var(--color-text-third, #888);
}

/* ─── 빈 상태 ─── */
.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--color-text-third, #666);
}

.empty i {
  font-size: 48px;
  opacity: 0.4;
}

.empty p {
  font-size: var(--font-size-base);
  margin: 0;
}

/* ─── 태블릿 (1024px 이하) ─── */
@media (max-width: 1024px) {
  .dialog > .paper {
    width: 95%;
    max-width: 480px;
    height: 85%;
    padding: 16px;
  }

  ul.notice-list > li {
    padding: 12px 14px;
    gap: 12px;
  }

  .avatar {
    width: 44px;
    height: 44px;
    border-radius: 12px;
  }
}

/* ─── 모바일 (768px 이하) ─── */
@media (max-width: 768px) {
  .dialog > .paper {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    border: none;
    padding: 12px 16px;
  }

  .header {
    margin-bottom: 12px;
  }

  ul.notice-list {
    gap: 6px;
  }

  ul.notice-list > li {
    padding: 12px;
    border-radius: 12px;
    gap: 12px;
  }

  .avatar {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    font-size: 18px;
  }
}

/* ─── 작은 모바일 (480px 이하) ─── */
@media (max-width: 480px) {
  .dialog > .paper {
    padding: 10px 12px;
  }

  .header > h2 {
    font-size: var(--font-size-lg);
  }

  ul.notice-list > li {
    padding: 10px;
    gap: 10px;
  }

  .avatar {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    font-size: 16px;
  }

  .content .message {
    font-size: var(--font-size-sm);
  }

  .content .time {
    font-size: var(--font-size-xs);
  }
}
</style>